import { FIELD_NAME_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { Alert } from "PFComponents/alert";
import { AvailabilityRangeCalendar } from "PFComponents/availability";
import { NumberInput } from "PFComponents/availability/rules_carousel/number_input";
import { Button } from "PFComponents/button";
import { Inline, Stack } from "PFComponents/containers";
import SidePanel from "PFComponents/side_panel/side_panel";
import { Typography } from "PFComponents/typography";
import { AvailabilityMode, StandardRangeV2 } from "PFCore/types";
import { DurationMode } from "PFTypes/booking";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import css from "./availability_table.module.scss";
import { CalendarDuration } from "./types";
import { HOURS_TO_MINUTES, MAX_HOURS_PER_WEEK } from "./utils";

type DurationSidePanelProps = {
  show: boolean;
  roleFormKey: string | null;
  rolesCount: number;
  duration?: CalendarDuration;
  onClose?: () => void;
  onApply?: (roleFormKey: string, availability: StandardRangeV2, applyToAll: boolean) => void;
};

export const DurationSidePanel = ({
  show,
  roleFormKey,
  rolesCount,
  duration,
  onClose,
  onApply
}: DurationSidePanelProps) => {
  const { t: tTranslation } = useTranslation("translation");
  const { t } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });

  const roleName = useWatch({ name: `${roleFormKey}.${FIELD_NAME_KEY}` });

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [hours, setHours] = useState<number | null>(null);

  const isFormValid = roleFormKey && startDate !== null && endDate !== null && hours !== null;

  const clearState = () => {
    setStartDate(null);
    setEndDate(null);
    setHours(null);
  };

  const handleApply = (applyToAll = false) => {
    if (isFormValid) {
      const availability: StandardRangeV2 = {
        start: startDate,
        end: endDate,
        duration: {
          mode: DurationMode.Hours,
          value: hours * HOURS_TO_MINUTES
        }
      };
      onApply?.(roleFormKey, availability, applyToAll);
    }
    clearState();
    onClose?.();
  };

  const handleClose = () => {
    clearState();
    onClose?.();
  };

  const handleAvailabilityChange = (start: string | null, end: string | null) => {
    setStartDate(start);
    setEndDate(end);
  };

  const handleHoursChange = (value?: number) => {
    value && setHours(value);
  };

  return (
    <SidePanel
      show={show}
      onClose={handleClose}
      title={t("bulkAddHours.title")}
      subTitle={t("bulkAddHours.subtitle", { roleName: roleName || t("emptyRoleName") })}
      size="large"
      footerRenderer={() => (
        <Inline spread>
          <Button kind="secondary" text={tTranslation("cancel")} onClick={handleClose} />
          <Inline gap="spacingSm">
            <Button
              kind="secondary"
              text={t("bulkAddHours.addToAllRoles", { count: rolesCount })}
              onClick={() => handleApply(true)}
              disabled={!isFormValid}
            />
            <Button
              kind="primary"
              text={tTranslation("apply")}
              onClick={() => handleApply(false)}
              disabled={!isFormValid}
            />
          </Inline>
        </Inline>
      )}
    >
      <Stack gap="spacingLg">
        <Typography variant="bodyRegular">{t("bulkAddHours.disclaimer")}</Typography>
        <Alert kind="warning" title={t("bulkAddHours.warning")} />
        <Inline>
          <AvailabilityRangeCalendar
            start={startDate ?? undefined}
            end={endDate ?? undefined}
            hideTitle
            onChange={handleAvailabilityChange}
            mode={AvailabilityMode.PerWeek}
            maxDate={duration?.end}
            minDate={{
              start: duration?.start,
              end: duration?.end
            }}
          />
          <NumberInput
            className={css.durationInput}
            label={t("bulkAddHours.hoursPerWeek")}
            max={MAX_HOURS_PER_WEEK}
            onChange={handleHoursChange}
            controlledValue={hours ?? undefined}
          />
        </Inline>
      </Stack>
    </SidePanel>
  );
};
